import type { MutationOptions } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { deletePermanently } from '@jotta/grpc-connect-openapi/fileQuery'
import { restore } from '@jotta/grpc-connect-openapi/fileQuery'
import { delete$ } from '@jotta/grpc-connect-openapi/fileQuery'
import { callUnaryMethod } from '@connectrpc/connect-query'
import { authTransport } from '../transport'
import type { TrashActionType } from '@jotta/hooks/useBackgroundJobStore'
import {
  addBackgroundJob,
  getOngoingBackgroundJobs,
} from '@jotta/hooks/useBackgroundJobStore'
import Debug from 'debug'
import { GetPathRequest_Sort } from '@jotta/grpc-connect-openapi/esm/openapi/file/file.v2_pb'
import { delay } from '@jotta/utils/delay'
import { useGetPath } from './useGetPath'
import type { ToastStatus } from '@jotta/ui/ToastProvider'

const debug = Debug('jotta:files:emptyTrash')

const trashMutationMap = {
  DELETE_PERMANENTLY: deletePermanently,
  RESTORE: restore,
  MOVE_TO_TRASH: delete$,
}

type UseFileTrashMutationProps = {
  paths: string[]
  actionType: TrashActionType
  toastId?: string | number | undefined
}

export const useFileTrashMutation = <T>(options?: {
  onMutate?: MutationOptions<any, any, UseFileTrashMutationProps, T>['onMutate']
  onSettled?: MutationOptions<
    any,
    any,
    UseFileTrashMutationProps,
    T
  >['onSettled']
}) =>
  useMutation({
    onMutate: options?.onMutate,
    onSettled: options?.onSettled,
    mutationFn: async ({ paths, actionType }: UseFileTrashMutationProps) => {
      const promises = paths.map(path => {
        const log = debug.extend(`${actionType}:${path}`)
        log('Start')
        return callUnaryMethod(
          trashMutationMap[actionType],
          { path, supportsBackgroundJobs: true },
          { transport: authTransport },
        )
      })

      const results = await Promise.allSettled(promises)

      results
        .filter(result => result.status === 'fulfilled')
        .forEach(result => {
          const backgroundJobId = result.value.backgroundJob?.id
          if (backgroundJobId) {
            addBackgroundJob(actionType, backgroundJobId)
          }
        })

      const failed = results.filter(result => result.status === 'rejected')
      if (failed.length > 0) {
        throw new Error(`Operation failed for ${failed.length} items`)
      }

      while (getOngoingBackgroundJobs(actionType).length > 0) {
        await delay(1000)
      }
    },
  })

const useProcessEmptyTrash = async ({
  timestamp,
  processItems,
}: {
  timestamp: number
  processItems: ReturnType<typeof useFileTrashMutation>['mutateAsync']
}) => {
  const limit = 100
  const getPathResponse = await useGetPath.fetch(
    {
      path: '/trash',
      limit,
      sort: GetPathRequest_Sort.DATE_ASC,
      includeDeleted: true,
      includeChildren: true,
      includeThumb: false,
    },
    { transport: authTransport },
  )

  const childrenList =
    getPathResponse?.pathItem?.children.filter(
      child => child.deletedAtMillis <= timestamp,
    ) || []

  debug(
    'Delete %d paths %s',
    childrenList.length,
    childrenList.map(item => item.name).join('\n'),
  )

  await processItems({
    actionType: 'DELETE_PERMANENTLY',
    paths: childrenList.map(child => child.path),
  })

  return Boolean(getPathResponse?.cursor && childrenList.length === limit)
}

export const emptyTrashMutationKey = ['emptyTrash']
type EmptyTrashMutationSignal = {
  isCancelled: boolean
}
export const useEmptyTrashMutation = <
  T extends string | number | undefined,
>(options?: {
  onMutate?: (variables: EmptyTrashMutationSignal) => T
  onSettled?: (
    status: ToastStatus,
    toastId: string | number | undefined,
    variables: EmptyTrashMutationSignal,
  ) => void
}) => {
  const emptyTrashMutation = useFileTrashMutation()

  return useMutation({
    mutationKey: emptyTrashMutationKey,
    mutationFn: async (variables: EmptyTrashMutationSignal) => {
      const timestamp = new Date().valueOf()
      let shouldContinue
      do {
        // Check for cancellation before processing batch
        if (variables.isCancelled) {
          debug('Empty trash operation was cancelled')
          return
        }

        // Process a batch of items
        shouldContinue = await useProcessEmptyTrash({
          timestamp,
          processItems: async params => {
            if (!variables.isCancelled) {
              return await emptyTrashMutation.mutateAsync(params)
            }
          },
        })
      } while (shouldContinue)
    },
    onMutate: options?.onMutate,
    onSettled: (_, error, variables, context) => {
      options?.onSettled?.(error ? 'FAILED' : 'SUCCESS', context, variables)
    },
  })
}
